import React, {
  useCallback, useContext, useEffect, useRef, useState,
} from 'react'
import {
  Box,
  Text,
  Accordion,
  ThemeContextExtend,
  IcoMoon,
  useResponsiveWrapperContext,
  ResponsiveBox,
  AddIcon,
  RemoveIcon,
} from '@usap/uicomponents'
import seoIds from '../../../../config/seoIds'
import { LocalContext } from '../../../helpers/useLocalContext'
import { SearchContext } from '../../common/context/SearchContextProvider'
import { useVehicleContext } from '../../../contexts/vehicle.context'
import { getDefaultUrlParam, navigationWithVehicle } from '../../../helpers/queryFactory'
import urlDecode from '../../../helpers/urlDecode'
import TopBarFilterOptions from '../TopBarFilterOptions'
import SortBy from '../SortBy'
import UniversalCheckbox from '../UniversalCheckbox'
import AccordionTitle from './AccordionTitle'
import SelectedRefinements from './SelectedRefinements'
import RefineBody from './RefineBody'
import { isSEOPage } from '../../../helpers/searchBoxFactory'
import PageTypeIdentifier from '../../../helpers/PageTypeIdentifier'
import { isElementInViewport } from '../../../helpers'

const minusIcoMoon = () => <IcoMoon icon={RemoveIcon} color="dark-2" size="24px" />

const plusIcoMoon = () => <IcoMoon icon={AddIcon} color="dark-2" size="24px" />

const accordionTheme = {
  accordion: {
    heading: { level: '5' },
    icons: {
      collapse: minusIcoMoon,
      expand: plusIcoMoon,
    },
    border: {
      side: 'bottom',
      color: 'grey',
    },
  },
}

const URLSEQUENCE = ['year', 'make', 'model', 'submodel', 'engine']
// eslint-disable-next-line security/detect-unsafe-regex
// eslint-disable-next-line no-useless-escape
const regexToRemoveCurrentPage = /&currentpage(\=[^&]*)?(?=&|$)|^currentpage(\=[^&]*)?(&|$)/

let listingPageIsLoaded = false

export default function RefineAccordion(props) {
  const { toggleItemsPerPage } = props
  const {
    searchState: { products, sidebarStatus, pageType },
    setSearchContext,
  } = useContext(SearchContext)
  const {
    refinements, selectedPart, selectedBrand, selectedVehicle,
  } = products
  const { available = [], selected = [] } = refinements
  const { isDesktop } = useResponsiveWrapperContext()
  const refinementsIndexes = Array(isDesktop ? 4 : available.length)
    .fill()
    .map((_, index) => index)
  const [activeIndex, setActiveIndex] = useState(refinementsIndexes)
  const { localContext } = useContext(LocalContext)
  const { history } = localContext
  const { location } = history
  const vehicleState = useVehicleContext()
  const hasSelected = Boolean(selected && selected.length)
  const hasAvailable = Boolean(available && available.length)
  const hasRefinements = Boolean(hasSelected || hasAvailable)
  const desktopFlag = isDesktop
  const mobileFlag = !isDesktop && sidebarStatus
  const displayAccordion = hasAvailable && (mobileFlag || desktopFlag)
  const refinementSectionRef = useRef(null)

  const searchCaseNavigation = useCallback(
    link => {
      const query = link || `?${getDefaultUrlParam('search')}`
      return history.push(`/search${query}`)
    },
    [history],
  )

  const seoCaseNavigation = useCallback(
    () => {
      const { breadcrumbs } = __COMBINED_DATA__
      const breadcrumbElement = breadcrumbs[breadcrumbs.length - 2] || {}
      if (breadcrumbElement.value) {
        return history.push(breadcrumbElement.value)
      }
      return null
    },
    [history],
  )

  const toggleRefine = useCallback(
    (link, removedValue, pathname = '/search') => {
      PageTypeIdentifier.setIdentifier(window?.pageType || null)
      const checkforSingleRefinedItem = __BROWSER__ && removedValue && selected.length === 1

      if (pageType === 'mntsrp' && checkforSingleRefinedItem) {
        return seoCaseNavigation()
      }

      if (link.startsWith('/')) {
        return history.push(link)
      }

      if (!link) {
        return history.push('/')
      }
      if (checkforSingleRefinedItem) {
        if (pageType === 'search') {
          return searchCaseNavigation(link)
        }

        if (pageType !== '/search' && __COMBINED_DATA__) {
          return seoCaseNavigation()
        }
      }

      if (!selectedPart || !selectedBrand) {
        if (removedValue === selectedPart || removedValue === selectedBrand) {
          if (Object.keys(selectedVehicle).length) {
            const urlQuery = URLSEQUENCE.reduce((query, param) => {
              const { [param]: paramValue } = selectedVehicle
              return `${query}${paramValue ? `/${urlDecode(paramValue)}` : ''}`
            }, '/vehicle')
            return history.push(urlQuery)
          }
          return history.push('/')
        }
      }
      if (isSEOPage(pageType)) {
        return history.push(link)
      }
      const vehicleSelected = (
        (vehicleState && vehicleState.vehicleDB) || []
      ).find(element => element.selected)

      let queryInstance = navigationWithVehicle(link, vehicleSelected)
      queryInstance = queryInstance.replace(regexToRemoveCurrentPage, '')

      return history.push({
        pathname: window.location.pathname || pathname,
        search: queryInstance,
      })
    },
    [
      history,
      searchCaseNavigation,
      selected.length,
      selectedBrand,
      selectedPart,
      selectedVehicle,
      seoCaseNavigation,
      vehicleState,
    ],
  )

  useEffect(() => {
    const element = refinementSectionRef.current
    const isElementVisible = isElementInViewport(element)

    if (element && !isElementVisible) {
      if (listingPageIsLoaded) {
        refinementSectionRef.current.scrollIntoView({ behavior: 'smooth' })
      }

      if (!listingPageIsLoaded) {
        listingPageIsLoaded = true
      }
    }
  }, [])

  return (
    <Box width="100%" pad={isDesktop ? { right: 'medium' } : ''}>
      <Box direction="column">
        <ResponsiveBox fxIf={{ large: false, other: true }}>
          <AccordionTitle />
          <SortBy toggleItemsPerPage={toggleItemsPerPage} />
        </ResponsiveBox>

        <ResponsiveBox fxIf={{ large: hasRefinements, other: hasRefinements }}>
          <ResponsiveBox
            id="refinedItemsTitle"
            fxPad={{
              large: { bottom: 'small' },
              other: { horizontal: 'medium', top: 'medium' },
            }}
            ref={refinementSectionRef}
          >
            <Text weight="bold" color="dark-2">
              Refine by:
            </Text>
          </ResponsiveBox>

          <SelectedRefinements toggleRefine={toggleRefine} />
        </ResponsiveBox>

        <ResponsiveBox pad="medium" fxIf={{ large: false, other: true }}>
          <UniversalCheckbox toggleItemsPerPage={toggleItemsPerPage} />
        </ResponsiveBox>
      </Box>

      <ResponsiveBox fxIf={{ large: false, other: true }}>
        <TopBarFilterOptions />
      </ResponsiveBox>

      <ResponsiveBox
        fxPad={{ large: 'xsmall', other: { horizontal: 'medium' } }}
        fxIf={{ large: displayAccordion, other: displayAccordion }}
      >
        <ThemeContextExtend value={accordionTheme}>
          <Accordion
            id="SidebarAccordianList"
            multiple
            activeIndex={activeIndex}
            onActive={setActiveIndex}
            onClick={() => setSearchContext({ sidebarStatus: !sidebarStatus })}
          >
            {available &&
            available.map(({ name, values }, index) => {
              const showSearch = activeIndex.some(
                xIndex => xIndex === index,
              )
              const { [name]: refineData = [] } = selected
              const refineItemName = `${name.replace(' ', '-')}-${index}`
              const lowerCaseName = refineItemName.toString().toLowerCase()
              const mapKey = seoIds.refineItem.replace(
                '{{key}}',
                lowerCaseName,
              )

              return (
                <Box id={mapKey} key={mapKey}>
                  <RefineBody
                    refineItem={lowerCaseName}
                    parent={name}
                    placeholder={name}
                    checkList={values}
                    updateRefine={toggleRefine}
                    data={refineData || []}
                    showSearch={showSearch}
                  />
                </Box>
              )
            })}
          </Accordion>
        </ThemeContextExtend>
      </ResponsiveBox>
    </Box>
  )
}
